export enum ErrorLevel {
  Fatal = 'fatal',
  Error = 'error',
  Warning = 'warning',
  Log = 'log',
  Info = 'info',
  Debug = 'debug',
}

export type GetLoggableErrorContext = {
  url?: string;
  route?: string;
  platform?: string;
  level?: ErrorLevel;
};

export type LoggableError = {
  tags: Record<string, string | undefined>;
  contexts: Record<string, Record<string, unknown>>;
  extra: Record<string, unknown>;
  level: ErrorLevel;
  fingerprint?: string[];
};

const hasField = (
  value: unknown,
  key: string,
): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && key in value;
};

const readString = (value: unknown, key: string): string | undefined => {
  if (!hasField(value, key)) {
    return undefined;
  }
  const field = value[key];
  if (typeof field !== 'string') {
    return undefined;
  }
  return field;
};

const readObject = (
  value: unknown,
  key: string,
): Record<string, unknown> | undefined => {
  if (!hasField(value, key)) {
    return undefined;
  }
  const field = value[key];
  if (typeof field !== 'object' || field === null) {
    return undefined;
  }
  return field as Record<string, unknown>;
};

const serializeError = (error: unknown): string => {
  if (error === null || error === undefined) {
    return JSON.stringify({});
  }
  try {
    if (typeof error === 'object') {
      return JSON.stringify(error, Object.getOwnPropertyNames(error), 2);
    }
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

export const getLoggableError = (
  error: unknown,
  context: GetLoggableErrorContext = {},
): LoggableError => {
  const errorCode = readString(error, 'code');
  const errorMessage = readString(error, 'message');
  const customData = readObject(error, 'customData');

  const tags: Record<string, string | undefined> = {
    errorCode,
    errorMessage,
    url: context.url,
    route: context.route,
    platform: context.platform,
  };

  const contexts: Record<string, Record<string, unknown>> = {};
  if (errorCode !== undefined || customData !== undefined) {
    contexts.firestore = {
      code: errorCode,
      customData,
    };
  }

  return {
    tags,
    contexts,
    extra: {
      error: serializeError(error),
    },
    level: context.level ?? ErrorLevel.Error,
  };
};
